import * as HoverCard from "@radix-ui/react-hover-card";
import { Flex, Text } from "@radix-ui/themes";
import { useState } from "react";
import { FaChevronRight } from "react-icons/fa";
import { cn } from "@/lib/utils";
import { marketplaceTokens, menuTokens } from "@/lib/tokens";
import { SORT_OPTIONS } from "@/lib/marketplace-sort";
import type { MarketplaceSort } from "./types";

type SortDropdownProps = {
  value?: MarketplaceSort;
  onChange: (value: MarketplaceSort) => void;
  options?: { value: MarketplaceSort; label: string }[];
};

export default function SortDropdown({
  value,
  onChange,
  options = SORT_OPTIONS,
}: SortDropdownProps) {
  const [open, setOpen] = useState(false);

  const activeSort = value ?? "discover";
  const activeLabel =
    options.find((o) => o.value === activeSort)?.label ??
    SORT_OPTIONS.find((o) => o.value === activeSort)?.label ??
    "Discover";
  const isNonDefault = activeSort !== "discover";

  function handleSelect(sortValue: MarketplaceSort) {
    onChange(sortValue);
    setOpen(false);
  }

  return (
    <HoverCard.Root
      open={open}
      onOpenChange={setOpen}
      openDelay={200}
      closeDelay={100}
    >
      <HoverCard.Trigger asChild>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={cn(
            "flex items-center gap-[8px] text-left border border-black/20 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 focus-visible:ring-offset-black",
            marketplaceTokens.filterItem,
            isNonDefault
              ? "bg-[#ECEFF4] text-[#050C2B] border-black/40"
              : open
                ? marketplaceTokens.filterItemActive
                : marketplaceTokens.filterItemInactive,
          )}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <Text className="flex-1">{activeLabel}</Text>
          <FaChevronRight
            size={10}
            aria-hidden
            className={cn("transition-transform", { "rotate-90": open })}
          />
        </button>
      </HoverCard.Trigger>

      <HoverCard.Portal>
        <HoverCard.Content
          side="bottom"
          avoidCollisions={true}
          align="end"
          sideOffset={0}
          className={cn(menuTokens.panel, "w-[200px] flex flex-col gap-[2px]")}
          role="listbox"
          aria-label="Sort by"
          onInteractOutside={() => setOpen(false)}
        >
          {options.map((option) => {
            const isSelected = activeSort === option.value;
            return (
              <Flex
                key={option.value}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                onClick={() => handleSelect(option.value as MarketplaceSort)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelect(option.value as MarketplaceSort);
                  }
                }}
                className={cn(
                  menuTokens.item,
                  "focus-visible:ring-1 focus-visible:ring-brand-primary",
                  isSelected ? menuTokens.itemSelected : menuTokens.itemNeutral,
                )}
              >
                <Text>{option.label}</Text>
              </Flex>
            );
          })}
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
}
